/**
 * ForwardMessageModal — V1.9.29
 *
 * Slide-up sheet listing all threads (TeamTalk + DaddyTalk) so the user
 * can pick one and forward the currently-selected message.
 *
 * Props:
 *   open           : boolean
 *   onClose        : () => void
 *   sourceMessage  : { id, body, attachment?, sender_name } — for the preview
 *   context        : "teamtalk" | "daddytalk"    (drives the outgoing endpoint)
 *   onForwarded    : optional callback after successful send
 */
import React from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import BottomSheet from "@/components/BottomSheet";
import { PaperPlaneTilt, MagnifyingGlass, Users, ChatCircle } from "@phosphor-icons/react";

export default function ForwardMessageModal({ open, onClose, sourceMessage, context = "teamtalk", onForwarded }) {
    const [threads, setThreads] = React.useState({ team: [], daddy: [] });
    const [loading, setLoading] = React.useState(false);
    const [busyId, setBusyId] = React.useState(null);
    const [q, setQ] = React.useState("");

    React.useEffect(() => {
        if (!open) return;
        (async () => {
            setLoading(true);
            try {
                const [tt, dd] = await Promise.all([
                    api.get("/team-chat/threads").catch(() => ({ data: { threads: [] } })),
                    api.get("/daddytalk/threads").catch(() => ({ data: { items: [] } })),
                ]);
                setThreads({
                    team: tt.data?.threads || tt.data?.items || [],
                    daddy: dd.data?.threads || dd.data?.items || [],
                });
            } finally { setLoading(false); }
        })();
    }, [open]);

    if (!open) return null;
    const body = sourceMessage?.body || "";
    const attKind = sourceMessage?.attachment?.kind
        || (sourceMessage?.attachment?.content_type?.startsWith("image/") && "image")
        || (sourceMessage?.attachment ? "file" : null);
    const previewText = body
        || (attKind === "voice" ? "🎤 Voice note"
        : attKind === "image" ? "🖼️ Photo"
        : attKind === "video" ? "🎬 Video"
        : attKind ? "📎 Attachment" : "Message");

    const forwardTo = async (target, kind) => {
        setBusyId(target.id);
        try {
            const targetEndpoint = kind === "team"
                ? `/team-chat/threads/${target.id}/messages/forward`
                : `/daddytalk/threads/${target.id}/messages/forward`;
            // V1.9.31 — always use the forward endpoint so attachments clone properly.
            await api.post(targetEndpoint, {
                source_message_id: sourceMessage.id,
                source_context: context,
            });
            toast.success(`Forwarded to ${target.name || target.counterparty?.name || "thread"}`);
            onForwarded?.();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't forward message.");
        } finally {
            setBusyId(null);
        }
    };

    const filterThread = (t) => {
        if (!q) return true;
        const name = (t.name || t.counterparty?.name || "").toLowerCase();
        return name.includes(q.toLowerCase());
    };

    return (
        <BottomSheet
            open={open}
            onClose={onClose}
            title={
                <span className="inline-flex items-center gap-2">
                    <PaperPlaneTilt size={16} weight="bold" className="text-cobalt" />
                    Forward message
                </span>
            }
        >
            <div className="px-4 pb-2">
                {/* Source preview */}
                <div className="mb-3 p-3 border-l-4 border-cobalt bg-cobalt/5 rounded-r-md">
                    <p className="text-[11px] font-bold text-cobalt uppercase tracking-wider mb-1">Forwarding</p>
                    <p className="text-sm text-ink/80 line-clamp-2">{previewText}</p>
                    {attKind && (
                        <p className="text-[10px] italic text-ink/50 mt-1">
                            Attachment will be shared as-is in the target thread.
                        </p>
                    )}
                </div>

                {/* Search */}
                <div className="relative mb-3">
                    <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search threads…"
                        data-testid="forward-search"
                        className="w-full h-10 pl-9 pr-3 bg-white text-[14px] focus:outline-none rounded-full shadow-sm ring-1 ring-ink/10 placeholder:text-ink/40"
                    />
                </div>

                {loading && <p className="text-xs text-ink/50 text-center py-6">Loading threads…</p>}

                {/* TeamTalk section */}
                {!loading && threads.team.filter(filterThread).length > 0 && (
                    <>
                        <p className="text-[10px] font-black uppercase tracking-wider text-ink/40 mt-2 mb-1">
                            <Users size={11} weight="bold" className="inline mr-1" />
                            TeamTalk ({threads.team.filter(filterThread).length})
                        </p>
                        <div className="space-y-1">
                            {threads.team.filter(filterThread).slice(0, 30).map((t) => (
                                <ThreadRow
                                    key={t.id} thread={t} kind="team"
                                    busy={busyId === t.id}
                                    disabled={busyId !== null}
                                    onPick={forwardTo}
                                />
                            ))}
                        </div>
                    </>
                )}

                {/* DaddyTalk section */}
                {!loading && threads.daddy.filter(filterThread).length > 0 && (
                    <>
                        <p className="text-[10px] font-black uppercase tracking-wider text-ink/40 mt-4 mb-1">
                            <ChatCircle size={11} weight="bold" className="inline mr-1" />
                            DaddyTalk ({threads.daddy.filter(filterThread).length})
                        </p>
                        <div className="space-y-1">
                            {threads.daddy.filter(filterThread).slice(0, 30).map((t) => (
                                <ThreadRow
                                    key={t.id} thread={t} kind="daddy"
                                    busy={busyId === t.id}
                                    disabled={busyId !== null}
                                    onPick={forwardTo}
                                />
                            ))}
                        </div>
                    </>
                )}

                {!loading && !threads.team.length && !threads.daddy.length && (
                    <p className="text-xs text-ink/50 text-center py-6">No threads to forward to.</p>
                )}
            </div>
        </BottomSheet>
    );
}

function ThreadRow({ thread, kind, busy, disabled, onPick }) {
    const label = thread.name
        || thread.counterparty?.name
        || (thread.is_group ? "Group" : "Chat");
    const sub = thread.is_group
        ? `${thread.member_ids?.length || 0} members`
        : thread.counterparty?.role || (kind === "daddy" ? "Candidate" : "Teammate");
    return (
        <button
            type="button"
            onClick={() => onPick(thread, kind)}
            disabled={disabled}
            data-testid={`forward-target-${thread.id}`}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-ink/5 disabled:opacity-40 transition text-left"
        >
            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-white text-sm font-black
                ${kind === "team" ? "bg-emerald-500" : "bg-cobalt"}`}>
                {(label || "?").trim().charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{label}</p>
                <p className="text-[11px] text-ink/50 truncate">{sub}</p>
            </div>
            {busy ? (
                <span className="text-[10px] text-cobalt">Sending…</span>
            ) : (
                <PaperPlaneTilt size={14} weight="bold" className="text-ink/40" />
            )}
        </button>
    );
}
